/* ==========================================================================
   Custom Page Transitions
   --------------------------------------------------------------------------
   Barba.js + GSAPによるページ遷移。
   - ブランドカラーの幕が下からせり上がり、ロゴをチラ見せして上へ抜ける
   - 次ページのコンテンツがフワッとフェードイン
   - Shopify特有のJS再発火、アナリティクス、スクロールリセットに対応

   Ver.1.1 perf での変更:
   - 遷移後のコンテナ内にある <script> を再実行する(reexecuteScripts)。
     Barba は差し替えた HTML のスクリプトを実行しないため、ヒーローや
     スタックスクロールなどセクション固有の JS が内部遷移後に動かなかった。
   - 遷移完了後に ScrollTrigger.refresh() を呼び、スクロール連動の位置を再計算する。

   Ver.1.5 での変更:
   - Barba は旧コンテナを enter 完了後まで DOM に残し、新コンテナをその下に
     追加する。そのままだと幕を引いた瞬間に見えるのは旧ページで、enter 完了後に
     旧コンテナが消えて新ページへガクッと切り替わっていた(「幕を引いたのに
     まだ遷移していない」の直接の原因)。beforeEnter で旧コンテナを先に
     非表示にして、新コンテナを画面の先頭に出す。
   - 遷移開始時に、開いたままの UI を閉じる(closeOpenUI)。
     ヘッダー(ALD ヘッダーのドロワー / 検索バー)は Barba のコンテナ外に
     あるため、ページが変わっても状態が引き継がれ、ドロワーが開いたまま・
     body が position:fixed のままでスクロールできなくなっていた。
     Horizon の <dialog>(検索モーダル等)と two-cart のカートパネルも同様に閉じる。
   - 幕を引くタイミングを「次ページの準備が整ってから」に変更(waitForNextPage)。
     次コンテナ内の CSS / 外部 JS / 画面内の画像・動画 / フォントの読み込みを
     待ってから幕を上げる(上限 READY_MAX_WAIT ms。それを超えたら待たずに上げる)。
   - 次ページの HTML を受け取った時点(幕が上がっている最中)で、画面上部の
     画像・CSS・JS を先読みして待ち時間を短くする(prefetchFromHtml)。
   - 幕が抜けきる前にコンテンツのフェードインが終わるようタイミングを調整。
   - 遷移が途中で止まった場合に状態を復旧する保険(watchdog)を追加。
   ========================================================================== */

(function () {
  'use strict';

  // ------------------------------------------------------------------
  // 0. 調整用の定数
  // ------------------------------------------------------------------
  const READY_MAX_WAIT = 1800; // ms: 次ページの読み込みを待つ上限(幕はロゴを見せたまま待つ)
  const PREFETCH_IMAGES = 12;  // 次ページ HTML の先頭から先読みする画像の数
  const WATCHDOG_MS = 12000;   // ms: 遷移がこの時間内に終わらなければ強制的に状態を復旧

  // ------------------------------------------------------------------
  // 1. 起動条件のチェック
  // ------------------------------------------------------------------

  // テーマエディタのプレビュー画面では動作させない（Shopify UIとの競合回避）
  if (window.Shopify && window.Shopify.designMode) {
    return;
  }

  // ライブラリの読み込み待ち（deferでも稀に順序がずれるケースを保険）
  function ready() {
    if (typeof window.barba === 'undefined' || typeof window.gsap === 'undefined') {
      return window.setTimeout(ready, 50);
    }
    init();
  }

  // 動きを抑えたいユーザー向けに、duration を短縮する係数
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const SPEED = prefersReducedMotion ? 0.3 : 1;

  // ------------------------------------------------------------------
  // 2. 差し替えたコンテナ内の <script> を再実行する
  //    - type が JSON など実行対象外のものはスキップ
  //    - 同じ src の module はブラウザが 1 度しか評価しないので安全
  //    - 戻り値: 外部スクリプト(src あり)の読み込み完了を待つ Promise の配列
  // ------------------------------------------------------------------
  function reexecuteScripts(container) {
    const pending = [];
    if (!container) return pending;
    const scripts = Array.prototype.slice.call(container.querySelectorAll('script'));
    scripts.forEach((old) => {
      const type = (old.getAttribute('type') || '').trim().toLowerCase();
      if (type && type !== 'text/javascript' && type !== 'application/javascript' && type !== 'module') {
        return; // application/json / application/ld+json など
      }
      try {
        const s = document.createElement('script');
        Array.prototype.forEach.call(old.attributes, (a) => {
          s.setAttribute(a.name, a.value);
        });
        if (old.src) {
          pending.push(
            new Promise((resolve) => {
              s.addEventListener('load', resolve, { once: true });
              s.addEventListener('error', resolve, { once: true });
            })
          );
        } else {
          s.textContent = old.textContent;
        }
        old.parentNode.replaceChild(s, old);
      } catch (e) {
        console.warn('[barba] script re-exec failed', e);
      }
    });
    return pending;
  }

  // ------------------------------------------------------------------
  // 3. 開いたままの UI を閉じる(ヘッダーは Barba コンテナの外にあるため)
  // ------------------------------------------------------------------
  function closeOpenUI() {
    // 3-1. ALD ヘッダー(ドロワー / 検索バー)
    try {
      if (window.ALDHeader && typeof window.ALDHeader.closeAll === 'function') {
        window.ALDHeader.closeAll();
      } else {
        // ヘッダー側に API が無い場合の直接操作(sections/ald-header.liquid と同じ手順)
        const header = document.querySelector('[data-ald-header]');
        if (header) {
          const nav = header.querySelector('[data-ald-nav]');
          const toggle = header.querySelector('[data-ald-toggle]');
          const search = header.querySelector('[data-ald-search]');
          const searchToggle = header.querySelector('[data-ald-search-toggle]');
          const wasOpen = nav && nav.classList.contains('is-open');
          header.querySelectorAll('[data-ald-sub]').forEach((a) => {
            a.setAttribute('aria-expanded', 'false');
            a.classList.remove('is-open');
          });
          header.querySelectorAll('[data-ald-submenu]').forEach((s) => s.classList.remove('is-active'));
          if (nav) nav.classList.remove('is-open');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
          if (search) search.classList.remove('is-open');
          if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('ald-menu-open', 'ald-locked');
          if (wasOpen) {
            const top = parseInt(document.body.style.top, 10);
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (!isNaN(top)) window.scrollTo(0, -top);
          }
        }
      }
    } catch (e) { /* noop */ }

    // 3-2. Horizon の <dialog>(検索モーダル / クイック追加など)
    try {
      document.querySelectorAll('dialog-component').forEach((component) => {
        const dialog = component.querySelector('dialog');
        if (!dialog || !dialog.open) return;
        if (typeof component.closeDialog === 'function') component.closeDialog();
        else dialog.close();
      });
      document.querySelectorAll('dialog[open]').forEach((dialog) => {
        try { dialog.close(); } catch (e) { /* noop */ }
      });
      document.documentElement.removeAttribute('scroll-lock');
    } catch (e) { /* noop */ }

    // 3-3. two-cart のカートパネル / 採寸パネル
    try {
      document.querySelectorAll('.tc-drawer.is-open, .tc-measure-panel.is-open').forEach((panel) => {
        const close = panel.querySelector('[data-tc-close]');
        if (close) close.click();
        else panel.classList.remove('is-open');
      });
      if (!document.querySelector('.tc-drawer.is-open, .tc-measure-panel.is-open')) {
        document.body.classList.remove('tc-scroll-locked');
        const overlay = document.querySelector('[data-tc-overlay]');
        if (overlay) overlay.classList.remove('is-open');
      }
    } catch (e) { /* noop */ }
  }

  // ------------------------------------------------------------------
  // 4. 次ページの先読み(HTML 受信直後 = 幕が上がっている最中に開始)
  // ------------------------------------------------------------------
  const prefetched = new Set();
  let keepAlive = []; // 先読み中の Image をGCされないように保持

  function prefetchFromHtml(html) {
    let doc;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return;
    }
    const container = doc.querySelector('[data-barba="container"]') || doc.body;
    if (!container) return;

    // CSS / 外部 JS を preload
    container.querySelectorAll('link[rel="stylesheet"][href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || prefetched.has(href)) return;
      prefetched.add(href);
      const pre = document.createElement('link');
      pre.rel = 'preload';
      pre.as = 'style';
      pre.href = href;
      document.head.appendChild(pre);
    });
    container.querySelectorAll('script[src]').forEach((script) => {
      const src = script.getAttribute('src');
      if (!src || prefetched.has(src)) return;
      prefetched.add(src);
      const pre = document.createElement('link');
      if ((script.getAttribute('type') || '').trim().toLowerCase() === 'module') {
        pre.rel = 'modulepreload';
      } else {
        pre.rel = 'preload';
        pre.as = 'script';
      }
      pre.href = src;
      document.head.appendChild(pre);
    });

    // 画面上部に来る画像を先読み(HTML の先頭から PREFETCH_IMAGES 枚)
    const imgs = Array.prototype.slice
      .call(container.querySelectorAll('img[src], img[srcset]'))
      .slice(0, PREFETCH_IMAGES);
    imgs.forEach((img) => {
      const key = img.getAttribute('srcset') || img.getAttribute('src');
      if (!key || prefetched.has(key)) return;
      prefetched.add(key);
      const pre = new Image();
      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      const src = img.getAttribute('src');
      if (sizes) pre.sizes = sizes;
      if (srcset) pre.srcset = srcset;
      if (src) pre.src = src;
      keepAlive.push(pre);
    });
  }

  // ------------------------------------------------------------------
  // 5. 次ページの準備が整うのを待つ(幕を引く前に呼ぶ)
  // ------------------------------------------------------------------
  function nearViewport(el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    if (rect.width === 0 && rect.height === 0) return false;
    return rect.bottom > -vh * 0.25 && rect.top < vh * 1.25;
  }

  function whenImageReady(img) {
    const settle = () => (img.decode ? img.decode().catch(() => {}) : Promise.resolve());
    if (img.complete) return img.naturalWidth > 0 ? settle() : Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener('load', () => settle().then(resolve, resolve), { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function whenVideoReady(video) {
    if (video.readyState >= 2) return Promise.resolve(); // HAVE_CURRENT_DATA
    return new Promise((resolve) => {
      ['loadeddata', 'canplay', 'playing', 'error', 'abort', 'emptied'].forEach((ev) => {
        video.addEventListener(ev, resolve, { once: true });
      });
    });
  }

  function whenStyleReady(link) {
    if (link.sheet) return Promise.resolve();
    return new Promise((resolve) => {
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
    });
  }

  function nextFrames() {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      window.requestAnimationFrame(() => window.requestAnimationFrame(finish));
      window.setTimeout(finish, 300); // 非表示タブなどで rAF が止まっていても進める
    });
  }

  function waitForNextPage(container, extraTasks, maxWait) {
    const tasks = (extraTasks || []).slice();

    // セクションが持ち込む CSS(two-cart.css など)
    container.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      tasks.push(whenStyleReady(link));
    });

    // Web フォント
    if (document.fonts && document.fonts.ready) {
      tasks.push(document.fonts.ready.catch(() => {}));
    }

    // 画面内(＋少し下)の画像。lazy のものは即時読み込みに切り替える
    container.querySelectorAll('img').forEach((img) => {
      if (!nearViewport(img)) return;
      if (img.loading === 'lazy') img.loading = 'eager';
      tasks.push(whenImageReady(img));
    });

    // 画面内の動画(src が決まっているものだけ。ヒーローのように後から
    // src を入れるものはポスターが出ていればよいので待たない)
    container.querySelectorAll('video').forEach((video) => {
      if (!nearViewport(video)) return;
      if (!video.getAttribute('src') && !video.querySelector('source[src]')) return;
      tasks.push(whenVideoReady(video));
    });

    const all = Promise.all(tasks).catch(() => {});
    const cap = new Promise((resolve) => window.setTimeout(resolve, maxWait));
    return Promise.race([all, cap]).then(nextFrames);
  }

  function refreshScrollTrigger() {
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      try { window.ScrollTrigger.refresh(); } catch (e) { /* noop */ }
    }
  }

  // ------------------------------------------------------------------
  // 6. Barba 初期化
  // ------------------------------------------------------------------

  function init() {
    const overlay = document.querySelector('.page-transition');
    const logo = document.querySelector('.page-transition__logo');

    if (!overlay || !logo) {
      console.warn('[barba] .page-transition が見つかりません。snippets/page-transition.liquid を <body> 直下に配置してください。');
      return;
    }

    let pendingScripts = []; // beforeEnter で再実行した外部スクリプトの読み込み待ち
    let watchdog = null;

    // 遷移が途中で止まった場合の保険: 幕とコンテナの状態を初期化する
    function recover() {
      watchdog = null;
      document.documentElement.classList.remove('is-transitioning');
      gsap.set(overlay, { y: '100%' });
      gsap.set(logo, { opacity: 0, y: 30 });
      document.querySelectorAll('[data-barba="container"]').forEach((c) => {
        gsap.set(c, { clearProps: 'transform,opacity' });
      });
    }
    function armWatchdog() {
      if (watchdog) window.clearTimeout(watchdog);
      watchdog = window.setTimeout(recover, WATCHDOG_MS);
    }
    function clearWatchdog() {
      if (watchdog) window.clearTimeout(watchdog);
      watchdog = null;
    }

    barba.init({
      debug: false,
      timeout: 5000,
      preventRunning: true, // 遷移中のクリックは無視(二重遷移の防止)

      // ----- 除外条件 -----
      prevent: ({ el, event, href }) => {
        if (!el) return false;

        // 明示的に除外したいリンク
        if (el.classList && el.classList.contains('no-barba')) return true;
        if (el.hasAttribute('data-no-barba')) return true;

        // 別タブで開くリンクは遷移演出と相性が悪い
        if (el.getAttribute('target') === '_blank') return true;

        // download 属性付きは遷移ではなくファイル取得
        if (el.hasAttribute('download')) return true;

        // mailto / tel など外部スキーム
        const url = el.getAttribute('href') || href || '';
        if (/^(mailto:|tel:|sms:|javascript:)/i.test(url)) return true;

        // 同一ページ内アンカー
        if (url.startsWith('#')) return true;

        // 外部ドメイン
        try {
          const linkUrl = new URL(url, window.location.href);
          if (linkUrl.origin !== window.location.origin) return true;
        } catch (e) {
          return true;
        }

        // Shopifyの認証/チェックアウト/カート系は標準遷移に任せる
        if (/^\/(cart|checkout|account|orders|password|challenge|admin)/i.test(
          new URL(url, window.location.href).pathname
        )) {
          return true;
        }

        // Cmd/Ctrl/Shift クリック（別タブ・別ウィンドウ）はブラウザ標準動作
        if (event && (event.metaKey || event.ctrlKey || event.shiftKey)) {
          return true;
        }

        return false;
      },

      // ----- トランジション定義 -----
      transitions: [
        {
          name: 'overlay-transition',

          // ----- 離脱: 幕が下から上へ覆う -----
          leave(data) {
            return new Promise((resolve) => {
              document.documentElement.classList.add('is-transitioning');

              const tl = gsap.timeline({ onComplete: resolve });

              tl.set(overlay, { y: '100%' })
                .set(logo, { opacity: 0, y: 30 })
                .to(overlay, {
                  y: '0%',
                  duration: 1 * SPEED,
                  ease: 'power4.inOut',
                })
                .to(
                  logo,
                  {
                    opacity: 1,
                    y: 0,
                    duration: 1 * SPEED,
                    ease: 'power2.out',
                  },
                  '-=0.45'
                );
            });
          },

          // ----- 進入: 次ページの準備を待ってから幕が上へ抜け、新コンテンツがフェードイン -----
          enter(data) {
            const container = data.next.container;

            // 新しいコンテナを最初は透明＋少し下に(幕の裏で読み込みを進める)
            gsap.set(container, { opacity: 0, y: 24 });

            return waitForNextPage(container, pendingScripts, READY_MAX_WAIT).then(() => {
              pendingScripts = [];

              // 幕の裏でスクロール連動の位置を確定させてから見せる
              refreshScrollTrigger();

              return new Promise((resolve) => {
                const tl = gsap.timeline({
                  onComplete: () => {
                    // 遷移後に残る transform を外す(position: fixed の基準がズレるのを防ぐ)
                    gsap.set(container, { clearProps: 'transform,opacity' });
                    resolve();
                  },
                });

                tl.to(overlay, {
                  y: '-100%',
                  duration: 0.8 * SPEED,
                  ease: 'power4.inOut',
                  delay: 0.15 * SPEED, // ロゴをちらりと見せる「間」
                })
                  // 幕が抜けきる前にコンテンツが出揃うよう、幕と同時に開始して先に終える
                  .to(
                    container,
                    {
                      opacity: 1,
                      y: 0,
                      duration: 0.7 * SPEED,
                      ease: 'power2.out',
                    },
                    '<'
                  )
                  // 次の遷移に備えて初期位置へリセット
                  .set(overlay, { y: '100%' })
                  .set(logo, { opacity: 0, y: 30 });
              });
            });
          },
        },
      ],
    });

    // ------------------------------------------------------------------
    // 7. Hooks: Shopifyとの連携
    // ------------------------------------------------------------------

    // 遷移の開始時(クリック直後)
    barba.hooks.before((data) => {
      armWatchdog();

      // 開いたままのヘッダー・モーダルを閉じる(幕が上がるのと同時に閉じ始める)
      closeOpenUI();

      // 次ページの HTML が届き次第、画面上部の画像や CSS を先読みする
      try {
        const href = data && data.next && data.next.url && data.next.url.href;
        if (data && data.next && data.next.html) {
          prefetchFromHtml(data.next.html);
        } else if (href && barba.cache && typeof barba.cache.get === 'function') {
          const entry = barba.cache.get(href);
          const request = entry && entry.request;
          if (request && typeof request.then === 'function') {
            request
              .then((html) => { if (typeof html === 'string') prefetchFromHtml(html); })
              .catch(() => {});
          }
        }
      } catch (e) { /* noop */ }
    });

    // 新ページ進入の直前（新コンテナが DOM に追加された直後）
    barba.hooks.beforeEnter((data) => {
      const current = data.current && data.current.container;
      const next = data.next && data.next.container;

      // Barba は旧コンテナを enter 完了後まで残す(新コンテナはその下に追加される)。
      // 旧コンテナを先に非表示にして、新コンテナを画面の先頭に出す。
      // (Barba 自身が enter 完了後に旧コンテナを参照で削除するので、ここでは消さない)
      if (current && current !== next) {
        current.style.display = 'none';
        current.setAttribute('aria-hidden', 'true');
        current.removeAttribute('id'); // #MainContent の重複を避ける(フォーカス移動用)
      }

      window.scrollTo(0, 0);

      // 前ページの ScrollTrigger を片付け（参照先の DOM はもう表示されない）
      if (window.ScrollTrigger && typeof window.ScrollTrigger.getAll === 'function') {
        try {
          window.ScrollTrigger.getAll().forEach((st) => {
            const trg = st.trigger;
            if (!trg) return;
            const stale =
              !document.body.contains(trg) ||
              (current && current !== next && current.contains(trg));
            if (stale) st.kill();
          });
        } catch (e) { /* noop */ }
      }

      // セクション固有のスクリプトを新コンテナで実行する
      pendingScripts = reexecuteScripts(next);
    });

    // 進入アニメーションの完了後（ここで再発火）
    barba.hooks.after((data) => {
      clearWatchdog();
      keepAlive = [];
      document.documentElement.classList.remove('is-transitioning');

      // 1) アナリティクス
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib) {
        try {
          window.ShopifyAnalytics.lib.page();
        } catch (e) {
          /* noop */
        }
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }

      // 2) <title> の更新（Barbaが対応していないSEOメタの追従用フック）
      //    &ndash; などの HTML エンティティは textarea 経由で復号する(そのまま入れると
      //    タブに「&ndash;」と表示されてしまう)
      const nextTitle = data.next.html.match(/<title[^>]*>([^<]*)<\/title>/i);
      if (nextTitle && nextTitle[1]) {
        const decoder = document.createElement('textarea');
        decoder.innerHTML = nextTitle[1];
        document.title = decoder.value.trim();
      }

      // 3) カスタムエレメントの再認識
      //    Dawn/Horizon系テーマは独自のCustom Elementで動的UIを構築しているため、
      //    新DOMに対してアップグレード処理を流す。
      if (window.customElements && typeof window.customElements.upgrade === 'function') {
        try {
          window.customElements.upgrade(data.next.container);
        } catch (e) {
          /* noop */
        }
      }

      // 4) スクロール連動アニメーションの位置を再計算
      refreshScrollTrigger();

      // 5) フォーカスをメインへ（アクセシビリティ）
      const main = document.querySelector('#MainContent');
      if (main) main.focus({ preventScroll: true });
    });

    // bfcache から戻ってきた時も、開きっぱなしの UI と幕を初期化する
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        closeOpenUI();
        recover();
      }
    });
  }

  // DOMContentLoaded を待ってから起動
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();